import { useQuery } from "@tanstack/react-query";
import api from "./api";

// Action detail for the recent-activity expand (BFF: GET api/actions/{id}, backed by
// action-service). Returns { actionId, status, title, summary, steps: [{ stepType, description, executeAfter }] }.
async function fetchActionDetail(actionId, { signal } = {}) {
  const { data } = await api.get(`api/actions/${actionId}`, { signal });
  return data;
}

// Lazy: fetches only once the row is expanded, so the feed stays cheap by default.
function useActionDetail(actionId, enabled) {
  return useQuery({
    queryKey: ["actionDetail", actionId],
    queryFn: ({ signal }) => fetchActionDetail(actionId, { signal }),
    enabled: !!actionId && enabled,
    staleTime: 60_000,
  });
}

// A payment plan's instalments for the calendar plan-detail timeline (BFF: GET
// api/actions/{id}/instalments). Returns { instalments: [{ number, total, dueOn, amount, state,
// paidOn, paymentRequestId }] }, one per scheduled send in order; state is paid|sent|scheduled|cancelled.
async function fetchActionInstalments(actionId, { signal } = {}) {
  const { data } = await api.get(`api/actions/${actionId}/instalments`, { signal });
  return data;
}

// Fetched only while the plan modal is open. The BFF joins each sent instalment to its payment
// request, so this is heavier than the plain action detail — keep it lazy.
function useActionInstalments(actionId, enabled) {
  return useQuery({
    queryKey: ["actionInstalments", actionId],
    queryFn: ({ signal }) => fetchActionInstalments(actionId, { signal }),
    enabled: !!actionId && enabled,
    staleTime: 30_000,
  });
}

// Cancels an in-flight action directly on the action-service (BFF: POST api/actions/{id}/cancel).
// Cancelling the action cancels ALL of its remaining steps — for a payment plan, every future
// instalment. 409 means the action is already terminal.
async function cancelScheduledAction(actionId) {
  const { data } = await api.post(`api/actions/${actionId}/cancel`);
  return data;
}

export {
  cancelScheduledAction,
  fetchActionDetail,
  fetchActionInstalments,
  useActionDetail,
  useActionInstalments,
};
